import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Container,
    Box,
    Typography,
    Paper,
    Card,
    CardMedia,
    CardContent,
    TextField,
    InputAdornment,
    Chip,
    IconButton,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    Divider,
    Snackbar,
    Alert,
    Select,
    MenuItem,
    FormControl,
    Pagination,
    Stack
} from '@mui/material';
import {
    ShoppingBag as ShoppingBagIcon,
    Search as SearchIcon,
    Visibility as VisibilityIcon,
    Close as CloseIcon,
    Star as StarIcon,
    TrendingUp as TrendingUpIcon,
    Bolt as FeaturedIcon,
    Diamond as LimitedEditionIcon
} from '@mui/icons-material';
import productService from '../services/productService';
import '../styles/ProtectedRoutes.css';

// Single set of mutually-exclusive tags — stored in one ProductTag column
const TAG_SELECT_OPTIONS = [
    { value: '', label: 'None' },
    { value: 'Bestseller', label: 'Bestseller', icon: StarIcon, color: '#ffd700', textColor: '#1a1a1a' },
    { value: 'Trending', label: 'Trending', icon: TrendingUpIcon, color: '#4fc3f7', textColor: '#08262e' },
    { value: 'Featured', label: 'Featured', icon: FeaturedIcon, color: '#ba68c8', textColor: '#210b28' },
    { value: 'Limited Edition', label: 'Limited Edition', icon: LimitedEditionIcon, color: '#ff8a65', textColor: '#2b0f04' }
];

const PAGE_SIZE = 12;

const AdminProducts = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1); // 1-indexed for MUI Pagination
    const [totalCount, setTotalCount] = useState(0);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [tagLoading, setTagLoading] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const searchDebounceRef = useRef(null);

    const FALLBACK_LG = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0yMDAgMTIwVjI4ME0xMjAgMjAwSDI4MCIgc3Ryb2tlPSIjOTk5OTk5IiBzdHJva2Utd2lkdGg9IjMiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIvPgo8L3N2Zz4K';

    // Debounce the search input so we don't hit the server on every keystroke
    useEffect(() => {
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setPage(1); // reset to first page on a new search
        }, 400);
        return () => clearTimeout(searchDebounceRef.current);
    }, [searchTerm]);

    const loadProducts = useCallback(async (pageNum) => {
        try {
            setLoading(true);
            const offset = (pageNum - 1) * PAGE_SIZE;

            // Keys here map directly to the query params your /api/products/admin route expects
            const response = await productService.getAdminProducts(
                {
                    category: 'Ready Made',
                    noLabel: true,
                    search: debouncedSearch || undefined
                },
                PAGE_SIZE,
                offset
            );

            if (response.success) {
                setProducts(response.data);
                setTotalCount(response.pagination?.total ?? 0);
            } else {
                setProducts([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error('Error loading products:', error);
            setProducts([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch]);

    useEffect(() => {
        loadProducts(page);
    }, [page, debouncedSearch, loadProducts]);

    const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

    const handlePageChange = (event, value) => {
        setPage(value);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getMediaUrl = (photoPath) => {
        if (!photoPath) return FALLBACK_LG;
        if (photoPath.startsWith('http')) return photoPath;
        const backendUrl = process.env.REACT_APP_API_URL?.replace('/api', '')
        return `${backendUrl}${photoPath}`;
    };

    const getStockStatus = (product) => {
        const stock = product.TotalSizeStock || product.QuantityInStock || product.Quantity || 0;

        if (stock > 10) {
            return { status: 'in-stock', text: 'IN STOCK', color: 'success' };
        } else if (stock > 0) {
            return { status: 'low-stock', text: `ONLY ${stock} LEFT`, color: 'warning' };
        } else {
            return { status: 'out-of-stock', text: 'OUT OF STOCK', color: 'error' };
        }
    };

    const handleTagSelectChange = async (product, newTagValue, event) => {
        if (event) event.stopPropagation();
        const previousTag = product.ProductTag || '';
        const loadingKey = `${product.ProductCode}_ProductTag`;

        const TAG_PRIORITY = { 'Bestseller': 1, 'Trending': 2, 'Featured': 3, 'Limited Edition': 4 };

        setTagLoading((prev) => ({ ...prev, [loadingKey]: true }));
        setProducts((prev) => {
            const updated = prev.map((p) =>
                p.ProductCode === product.ProductCode ? { ...p, ProductTag: newTagValue || null } : p
            );
            // Re-sort to match backend ordering: tagged first, in priority order
            return [...updated].sort((a, b) => {
                const aPriority = TAG_PRIORITY[a.ProductTag] || 5;
                const bPriority = TAG_PRIORITY[b.ProductTag] || 5;
                return aPriority - bPriority;
            });
        });
        setSelectedProduct((prev) =>
            prev && prev.ProductCode === product.ProductCode ? { ...prev, ProductTag: newTagValue || null } : prev
        );

        try {
            const response = await productService.updateProduct(product.ProductCode, {
                ProductTag: newTagValue || null
            });

            if (!response?.success) {
                throw new Error(response?.message || 'Failed to update tag');
            }
        } catch (error) {
            console.error('Error updating ProductTag:', error);
            setProducts((prev) =>
                prev.map((p) =>
                    p.ProductCode === product.ProductCode ? { ...p, ProductTag: previousTag || null } : p
                )
            );
            setSelectedProduct((prev) =>
                prev && prev.ProductCode === product.ProductCode ? { ...prev, ProductTag: previousTag || null } : prev
            );
            setSnackbar({ open: true, message: 'Failed to update tag', severity: 'error' });
        } finally {
            setTagLoading((prev) => ({ ...prev, [loadingKey]: false }));
        }
    };

    const handleViewDetails = (product) => {
        setSelectedProduct(product);
        setDetailsOpen(true);
    };

    const handleCloseDetails = () => {
        setDetailsOpen(false);
        setSelectedProduct(null);
    };

    if (loading && products.length === 0) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box display="flex" flexDirection="column" alignItems="center" gap={2} className="glass-content-container">
                    <CircularProgress size={40} className="glass-loading" />
                    <Typography variant="body1" className="glass-text-secondary">
                        Loading products...
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header */}
          
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                    <ShoppingBagIcon sx={{ color: '#bc965e', fontSize: 32 }} />
                    <Typography variant="h4" className="glass-title">
                        Product Management
                    </Typography>
                </Box>
                <Typography variant="body1" className="glass-text-secondary" sx={{mb:2}}>
                    {totalCount} ready-made product{totalCount !== 1 ? 's' : ''}
                </Typography>
         

            {/* Search */}
            <Paper elevation={1} className="glass-content-container" sx={{ mb: 3, p: 2 }}>
                <TextField
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            '& fieldset': {
                                borderColor: 'rgba(188, 150, 94, 0.3)',
                            },
                            '&:hover fieldset': {
                                borderColor: 'rgba(188, 150, 94, 0.5)',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#bc965e',
                            },
                        },
                        '& .MuiInputBase-input': {
                            color: '#ffffff',
                        },
                        '& .MuiInputBase-input::placeholder': {
                            color: 'rgba(255, 255, 255, 0.7)',
                            opacity: 1,
                        },
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#bc965e' }} />
                            </InputAdornment>
                        ),
                        endAdornment: loading ? (
                            <InputAdornment position="end">
                                <CircularProgress size={16} sx={{ color: '#bc965e' }} />
                            </InputAdornment>
                        ) : undefined
                    }}
                />
            </Paper>

            {/* Products Grid - 3 per row, responsive */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 3,
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                    minHeight: 200,
                    opacity: loading ? 0.5 : 1,
                    transition: 'opacity 0.2s ease'
                }}
            >
                {products.map((product) => {
                    const stockStatus = getStockStatus(product);
                    return (
                        <Box
                            key={product.ProductCode}
                            sx={{
                                flex: {
                                    xs: '1 1 100%',
                                    sm: '1 1 calc(50% - 12px)',
                                    md: '1 1 calc(33.333% - 16px)'
                                },
                                maxWidth: {
                                    xs: '100%',
                                    sm: 'calc(50% - 12px)',
                                    md: 'calc(33.333% - 16px)'
                                }
                            }}
                        >
                            <Card
                                elevation={0}
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    background: 'linear-gradient(145deg, rgba(30, 27, 24, 0.95) 0%, rgba(12, 11, 10, 0.98) 100%)',
                                    border: '1px solid rgba(188, 150, 94, 0.18)',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    transition: 'transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        borderColor: 'rgba(188, 150, 94, 0.45)',
                                        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.45)'
                                    }
                                }}
                            >
                                <Box sx={{ position: 'relative' }}>
                                    <CardMedia
                                        component="img"
                                        sx={{
                                            height: { xs: 180, sm: 170, md: 160 },
                                            objectFit: 'cover'
                                        }}
                                        image={getMediaUrl(product.Photo)}
                                        alt={product.ProductName}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = FALLBACK_LG;
                                        }}
                                    />

                                    <Box sx={{ position: 'absolute', top: 10, left: 10, display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                                        {product.ProductTag && (() => {
                                            const selectedTag = TAG_SELECT_OPTIONS.find((opt) => opt.value === product.ProductTag);
                                            if (!selectedTag) return null;
                                            return (
                                                <Chip
                                                    label={selectedTag.label.toUpperCase()}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: '0.6rem',
                                                        letterSpacing: '0.03em',
                                                        backgroundColor: `${selectedTag.color}e6`,
                                                        color: selectedTag.textColor
                                                    }}
                                                />
                                            );
                                        })()}
                                    </Box>

                                    <Chip
                                        label={stockStatus.text}
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            top: 10,
                                            right: 10,
                                            fontWeight: 700,
                                            fontSize: '0.65rem',
                                            letterSpacing: '0.03em',
                                            backgroundColor:
                                                stockStatus.status === 'in-stock'
                                                    ? 'rgba(46, 125, 50, 0.85)'
                                                    : stockStatus.status === 'low-stock'
                                                        ? 'rgba(237, 108, 2, 0.85)'
                                                        : 'rgba(211, 47, 47, 0.85)',
                                            color: '#ffffff'
                                        }}
                                    />
                                </Box>

                                <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 1.5, px: 2 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            color: '#bc965e',
                                            fontWeight: 700,
                                            letterSpacing: '0.12em',
                                            fontSize: '0.65rem',
                                            mb: 0.5
                                        }}
                                    >
                                        {(product.SubCategory || product.Category || '').toString().toUpperCase()}
                                    </Typography>

                                    <Typography
                                        variant="h6"
                                        sx={{
                                            color: '#ffffff',
                                            fontWeight: 600,
                                            fontSize: { xs: '0.95rem', sm: '1rem' },
                                            mb: 0.5,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}
                                    >
                                        {product.ProductName}
                                    </Typography>

                                    <Typography
                                        variant="h6"
                                        sx={{
                                            color: '#bc965e',
                                            fontWeight: 700,
                                            fontSize: { xs: '1.05rem', sm: '1.1rem' },
                                            mb: 0.5
                                        }}
                                    >
                                        ₹{product.Price?.toLocaleString()}
                                    </Typography>

                                    {product.Color && (
                                        <Typography
                                            variant="body2"
                                            sx={{ color: 'rgba(255, 255, 255, 0.85)', mb: 0.5 }}
                                        >
                                            Color: <Box component="span" sx={{ fontWeight: 700, color: '#ffffff' }}>{product.Color}</Box>
                                        </Typography>
                                    )}

                                    {product.Description && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'rgba(255, 255, 255, 0.6)',
                                                mb: 1,
                                                fontSize: '0.8rem',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}
                                        >
                                            {product.Description}
                                        </Typography>
                                    )}

                                    {product.SizeDetails && product.SizeDetails.length > 0 && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center', mb: 1.25 }}>
                                            {product.SizeDetails.map((sizeInfo, index) => (
                                                <Chip
                                                    key={index}
                                                    label={`${sizeInfo.Size} (${sizeInfo.QuantityInStock})`}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        fontSize: '0.65rem',
                                                        height: 22,
                                                        borderColor: 'rgba(188, 150, 94, 0.4)',
                                                        color: sizeInfo.QuantityInStock > 0 ? '#ffffff' : 'rgba(255,255,255,0.4)',
                                                        opacity: sizeInfo.QuantityInStock > 0 ? 1 : 0.5
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    )}

                                    {(!product.SizeDetails || product.SizeDetails.length === 0) && product.AvailableSizes && (
                                        <Typography
                                            variant="caption"
                                            sx={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)', mb: 2 }}
                                        >
                                            Sizes: {product.AvailableSizes}
                                        </Typography>
                                    )}

                                    <FormControl
                                        size="small"
                                        fullWidth
                                        sx={{ mb: 1.25 }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Select
                                            value={product.ProductTag || ''}
                                            onChange={(e) => handleTagSelectChange(product, e.target.value, e)}
                                            disabled={tagLoading[`${product.ProductCode}_ProductTag`]}
                                            displayEmpty
                                            sx={{
                                                fontSize: '0.75rem',
                                                height: 32,
                                                color: product.ProductTag ? '#ffffff' : 'rgba(255, 255, 255, 0.6)',
                                                backgroundColor: 'rgba(255, 255, 255, 0.04)',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(188, 150, 94, 0.3)'
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(188, 150, 94, 0.5)'
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#bc965e'
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    color: '#bc965e'
                                                }
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        backgroundColor: '#1a1714',
                                                        border: '1px solid rgba(188, 150, 94, 0.25)'
                                                    }
                                                }
                                            }}
                                        >
                                            {TAG_SELECT_OPTIONS.map((opt) => (
                                                <MenuItem
                                                    key={opt.value || 'none'}
                                                    value={opt.value}
                                                    sx={{
                                                        fontSize: '0.8rem',
                                                        color: opt.value ? opt.color : 'rgba(255, 255, 255, 0.6)',
                                                        fontWeight: opt.value ? 700 : 400
                                                    }}
                                                >
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<VisibilityIcon />}
                                        onClick={() => handleViewDetails(product)}
                                        sx={{
                                            borderColor: 'rgba(188, 150, 94, 0.5)',
                                            color: '#bc965e',
                                            fontWeight: 700,
                                            letterSpacing: '0.04em',
                                            borderRadius: 5,
                                            py: 0.5,
                                            fontSize: '0.75rem',
                                            '&:hover': {
                                                borderColor: '#bc965e',
                                                backgroundColor: 'rgba(188, 150, 94, 0.08)'
                                            }
                                        }}
                                    >
                                        QUICK VIEW
                                    </Button>
                                </CardContent>
                            </Card>
                        </Box>
                    );
                })}
            </Box>

            {products.length === 0 && !loading && (
                <Paper elevation={1} className="glass-empty-state">
                    <Box sx={{ mb: 2, fontSize: '4rem' }}>📦</Box>
                    <Typography variant="h6" gutterBottom className="glass-text-primary">
                        No products found
                    </Typography>
                    <Typography variant="body1" className="glass-text-secondary">
                        {searchTerm
                            ? 'Try adjusting your search.'
                            : 'No ready-made products available.'
                        }
                    </Typography>
                </Paper>
            )}

            {/* Pagination controls */}
            {/* Pagination controls */}
            {products.length > 0 && pageCount > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Stack spacing={2} alignItems="center">
                        <Pagination
                            count={pageCount}
                            page={page}
                            onChange={handlePageChange}
                            color="primary"
                            size="large"
                            showFirstButton
                            showLastButton
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    color: '#ffffff',
                                    borderColor: 'rgba(188, 150, 94, 0.3)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(188, 150, 94, 0.1)',
                                        borderColor: 'rgba(188, 150, 94, 0.5)',
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: '#bc965e',
                                        color: '#000000',
                                        '&:hover': {
                                            backgroundColor: '#d4a96a',
                                        },
                                    },
                                },
                            }}
                        />
                        <Typography variant="body2" className="glass-text-secondary" textAlign="center">
                            Showing {((page - 1) * PAGE_SIZE) + 1} - {Math.min(page * PAGE_SIZE, totalCount)} of {totalCount} products
                        </Typography>
                    </Stack>
                </Box>
            )}
            {/* Product Details Dialog */}
            <Dialog
                open={detailsOpen}
                onClose={handleCloseDetails}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        background: 'linear-gradient(145deg, rgba(35, 32, 28, 0.97) 0%, rgba(18, 17, 16, 0.98) 100%)',
                        backdropFilter: 'blur(24px)',
                        border: '1px solid rgba(212, 175, 55, 0.25)',
                        borderRadius: 3,
                        boxShadow: '0 8px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(188, 150, 94, 0.08) inset'
                    }
                }}
            >
                {selectedProduct && (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                            <IconButton onClick={handleCloseDetails} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <DialogContent
                            sx={{
                                pt: 0,
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                                '&::-webkit-scrollbar': {
                                    display: 'none'
                                }
                            }}
                        >
                            <Box sx={{ textAlign: 'center', mb: 2 }}>
                                <Box
                                    component="img"
                                    src={getMediaUrl(selectedProduct.Photo)}
                                    alt={selectedProduct.ProductName}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = FALLBACK_LG;
                                    }}
                                    sx={{
                                        width: '100%',
                                        minHeight: 200,
                                        maxHeight: 320,
                                        objectFit: 'contain',
                                        borderRadius: 2,
                                        backgroundColor: 'rgba(255, 255, 255, 0.04)',
                                        border: '1px solid rgba(188, 150, 94, 0.12)'
                                    }}
                                />
                            </Box>

                            <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 600, mb: 1, textAlign: 'center' }}>
                                {selectedProduct.ProductName}
                            </Typography>

                            <Typography variant="h6" sx={{ color: '#bc965e', fontWeight: 700, mb: 2, textAlign: 'center' }}>
                                ₹{selectedProduct.Price?.toLocaleString()}
                            </Typography>

                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                {(() => {
                                    const stockStatus = getStockStatus(selectedProduct);
                                    return (
                                        <Chip
                                            label={stockStatus.text}
                                            color={stockStatus.color}
                                            sx={{ fontWeight: 600 }}
                                        />
                                    );
                                })()}
                            </Box>

                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 1, textAlign: 'center' }}>
                                Tag
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 220, display: 'flex', mx: 'auto', mb: 2 }}>
                                <Select
                                    value={selectedProduct.ProductTag || ''}
                                    onChange={(e) => handleTagSelectChange(selectedProduct, e.target.value, e)}
                                    disabled={tagLoading[`${selectedProduct.ProductCode}_ProductTag`]}
                                    displayEmpty
                                    sx={{
                                        fontSize: '0.85rem',
                                        color: selectedProduct.ProductTag ? '#ffffff' : 'rgba(255, 255, 255, 0.6)',
                                        backgroundColor: 'rgba(255, 255, 255, 0.04)',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(188, 150, 94, 0.3)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(188, 150, 94, 0.5)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#bc965e'
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: '#bc965e'
                                        }
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: '#1a1714',
                                                border: '1px solid rgba(188, 150, 94, 0.25)'
                                            }
                                        }
                                    }}
                                >
                                    {TAG_SELECT_OPTIONS.map((opt) => (
                                        <MenuItem
                                            key={opt.value || 'none'}
                                            value={opt.value}
                                            sx={{
                                                fontSize: '0.85rem',
                                                color: opt.value ? opt.color : 'rgba(255, 255, 255, 0.6)',
                                                fontWeight: opt.value ? 700 : 400
                                            }}
                                        >
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Divider sx={{ borderColor: 'rgba(188, 150, 94, 0.2)', mb: 2 }} />

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                        Product Code
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#ffffff' }}>
                                        {selectedProduct.ProductCode}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                        Category
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#ffffff' }}>
                                        {selectedProduct.Category}
                                    </Typography>
                                </Box>
                                {selectedProduct.Description && (
                                    <Box>
                                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 0.5 }}>
                                            Description
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#ffffff' }}>
                                            {selectedProduct.Description}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {selectedProduct.SizeDetails && selectedProduct.SizeDetails.length > 0 && (
                                <>
                                    <Divider sx={{ borderColor: 'rgba(188, 150, 94, 0.2)', mb: 2 }} />
                                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 1 }}>
                                        Size & Stock Breakdown
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {selectedProduct.SizeDetails.map((sizeInfo, index) => (
                                            <Chip
                                                key={index}
                                                label={`${sizeInfo.Size} (${sizeInfo.QuantityInStock})`}
                                                variant="outlined"
                                                color={sizeInfo.QuantityInStock > 0 ? 'primary' : 'default'}
                                                sx={{ opacity: sizeInfo.QuantityInStock > 0 ? 1 : 0.5 }}
                                            />
                                        ))}
                                    </Box>
                                </>
                            )}
                        </DialogContent>
                    </>
                )}
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

        </Container>
    );
};

export default AdminProducts;